import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, EmptyState } from '../../components';
import { getMyStartup, uploadStartupDocument, StartupDoc, StartupDocument } from '../../services/startup-service';

const fmtSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Mock: in production, this would come from program enrollment data
const MINIMUM_PHASE = 5;

const PitchDeck = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [startup, setStartup] = useState<StartupDoc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  // Mock current phase - in production this comes from enrollment/program progress
  const [currentPhase] = useState(5);

  const [submissionStatus, setSubmissionStatus] = useState<'not_submitted' | 'submitted' | 'reviewed'>('not_submitted');
  const [mentorFeedback, setMentorFeedback] = useState<string[]>([]);

  useEffect(() => {
    getMyStartup()
      .then(s => {
        setStartup(s);
        // Check if a pitch deck document exists
        if (s?.documents?.some((d: StartupDocument) => d.title?.toLowerCase().includes('pitch'))) {
          setSubmissionStatus('submitted');
          // Mock feedback - in production this comes from the API
          setMentorFeedback([
            'Strong value proposition, but consider refining slide 3 with more specific market data.',
            'Financial projections look solid. Add customer acquisition cost breakdown.',
          ]);
          if (mentorFeedback.length > 0) setSubmissionStatus('reviewed');
        }
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    setError('');
    try {
      const updated = await uploadStartupDocument(file, 'Pitch Deck');
      setStartup(updated);
      setSubmissionStatus('submitted');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return <div className="section-gap"><p className="text-muted">Loading...</p></div>;
  }

  if (currentPhase < MINIMUM_PHASE) {
    return (
      <div className="section-gap">
        <EmptyState
          title="Pitch Deck Not Available Yet"
          description={`The pitch deck submission opens in Phase ${MINIMUM_PHASE}. You are currently in Phase ${currentPhase}. Complete the earlier phases to unlock this section.`}
        />
      </div>
    );
  }

  const pitchDocs = startup?.documents?.filter((d: StartupDocument) =>
    d.title?.toLowerCase().includes('pitch')
  ) || [];

  return (
    <div className="section-gap">
      <div>
        <h1 className="text-3xl font-bold text-text">Pitch Deck</h1>
        <p className="text-muted mt-1">Upload and manage your pitch deck for investor review</p>
      </div>

      {error && (
        <div className="bg-danger/10 border border-danger/30 text-danger text-sm rounded-lg px-4 py-3">
          {error}
        </div>
      )}

      {/* Submission Status */}
      <Card>
        <div className="flex items-center gap-4 mb-4">
          <h2 className="text-xl font-semibold text-text">Submission Status</h2>
          <span className={`text-xs px-3 py-1 rounded-full font-medium ${
            submissionStatus === 'reviewed' ? 'bg-success/10 text-success' :
            submissionStatus === 'submitted' ? 'bg-warning/10 text-warning' :
            'bg-surface text-muted'
          }`}>
            {submissionStatus === 'reviewed' ? 'Reviewed' :
             submissionStatus === 'submitted' ? 'Submitted - Awaiting Review' :
             'Not Submitted'}
          </span>
        </div>

        {submissionStatus === 'not_submitted' && (
          <p className="text-muted text-sm">
            Upload your pitch deck below to submit it for mentor review. Accepted formats: PDF, PPTX.
          </p>
        )}

        {submissionStatus === 'submitted' && (
          <p className="text-muted text-sm">
            Your pitch deck has been submitted. Mentors will review it and provide feedback.
          </p>
        )}

        {submissionStatus === 'reviewed' && (
          <p className="text-muted text-sm">
            Your pitch deck has been reviewed. Check the feedback section below.
          </p>
        )}
      </Card>

      {/* Upload Section */}
      <Card>
        <h3 className="text-lg font-semibold text-text mb-4">Upload Pitch Deck</h3>

        {pitchDocs.length > 0 && (
          <div className="space-y-2 mb-4">
            {pitchDocs.map((doc: StartupDocument) => (
              <div key={doc._id} className="flex items-center justify-between p-3 bg-surface rounded-lg border border-line">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <svg className="w-8 h-8 text-chart flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                  </svg>
                  <div className="min-w-0">
                    <div className="text-text text-sm font-medium truncate">{doc.title || doc.fileName}</div>
                    <div className="text-muted text-xs">
                      {doc.fileName} -- {fmtSize(doc.fileSize)} -- Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost" size="sm">Download</Button>
                </a>
              </div>
            ))}
          </div>
        )}

        <div className="border-2 border-dashed border-line rounded-lg p-8 text-center">
          <svg className="w-12 h-12 text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p className="text-muted text-sm mb-4">
            {pitchDocs.length > 0 ? 'Upload a new version to replace your current pitch deck' : 'Drag and drop your pitch deck or click to browse'}
          </p>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.pptx,.ppt"
            disabled={isUploading}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? 'Uploading...' : 'Choose File'}
          </Button>
          <p className="text-xs text-muted mt-2">PDF or PPTX, max 25 MB</p>
        </div>
      </Card>

      {/* Mentor Feedback Section */}
      {submissionStatus === 'reviewed' && mentorFeedback.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-text mb-4">Mentor Feedback</h3>
          <div className="space-y-3">
            {mentorFeedback.map((feedback, index) => (
              <div key={index} className="flex gap-3 p-3 bg-surface rounded-lg border border-line">
                <div className="flex-shrink-0 w-8 h-8 bg-accent-soft rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-chart" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm text-text">{feedback}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {submissionStatus !== 'reviewed' && submissionStatus === 'submitted' && (
        <Card>
          <h3 className="text-lg font-semibold text-text mb-2">Mentor Feedback</h3>
          <p className="text-muted text-sm">No feedback yet. Your pitch deck is awaiting review.</p>
        </Card>
      )}
    </div>
  );
};

export default PitchDeck;
